import { useSelector } from 'hooks';
import { getCurrencyIsoCode } from 'modules/selfServices/selectors';
import React from 'react';
import { useIntl } from 'react-intl';
import { IntlSupport } from 'utils/IntlSupport';

// We could live without this override - just to give more flexibility if one day we have another source in a specific context for isoCode
type PriceFormatterOptions = {
  currencyIso?: string;
};

type FormatPrice = (value: number | null | undefined, options?: PriceFormatterOptions) => string;

/**
 * Only used by the fallback path (engines without `signDisplay: 'negative'` support). Cached per
 * (locale, currency) so we don't allocate a throwaway Intl.NumberFormat on every format call.
 */
const maximumFractionDigitsCache = new Map<string, number>();

const getMaximumFractionDigits = (locale: string, options: Intl.NumberFormatOptions): number => {
  const cacheKey = `${locale}|${options.currency ?? ''}`;
  const cached = maximumFractionDigitsCache.get(cacheKey);
  if (cached !== undefined) {
    return cached;
  }

  const { maximumFractionDigits = 2 } = new Intl.NumberFormat(locale, options).resolvedOptions();
  maximumFractionDigitsCache.set(cacheKey, maximumFractionDigits);

  return maximumFractionDigits;
};

export const usePriceFormatter = (): FormatPrice => {
  const intl = useIntl();
  const currencyIsoFromStore = useSelector(getCurrencyIsoCode);

  return React.useCallback(
    (value, { currencyIso = currencyIsoFromStore } = {}) => {
      if (!Number.isFinite(value)) {
        return '';
      }

      const baseOptions = {
        currency: currencyIso,
        style: 'currency' as const,
      };

      if (IntlSupport.negative) {
        return intl.formatNumber(value!, { ...baseOptions, signDisplay: 'negative' });
      }

      // Fallback: `negative` hides the sign for values that round to zero (e.g. -0.001 €). Replicate
      // that by rounding first and zeroing the sign ourselves before formatting with default options.
      // Round the magnitude (not the signed value) so ties resolve the same way as Intl's default
      // `halfExpand` rounding mode — e.g. -0.005 € must round to -0.01 €, not to 0.
      const maximumFractionDigits = getMaximumFractionDigits(intl.locale, baseOptions);
      const factor = 10 ** maximumFractionDigits;
      const roundsToZero = Math.round(Math.abs(value!) * factor) / factor === 0;

      return intl.formatNumber(roundsToZero ? 0 : value!, baseOptions);
    },
    [intl, currencyIsoFromStore]
  );
};